import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, CreditCard, Clock, Info } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface PaymentPlanSelectorProps {
  totalAmount: number;
  onPlanSelected: (planType: 'full' | 'installments', installmentCount: number, dueDays: number) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

interface PaymentPlan {
  id: string;
  type: 'full' | 'installments';
  installmentCount: number;
  dueDays: number;
  title: string;
  description: string;
  features: string[];
  popular?: boolean;
}

export const PaymentPlanSelector: React.FC<PaymentPlanSelectorProps> = ({
  totalAmount,
  onPlanSelected,
  onCancel,
  isSubmitting = false
}) => {
  const { t, language } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState<string>('full-60');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Financial restrictions based on total amount
  const getMinimumAmount = (planId: string): number => {
    switch (planId) {
      case 'full-30':
        return 50000; // 50k SAR
      case 'full-60':
      case 'installments-2':
        return 100000; // 100k SAR
      case 'full-90':
      case 'installments-3':
        return 150000; // 150k SAR
      default:
        return 0;
    }
  };

  const isPlanEnabled = (planId: string): boolean => {
    return totalAmount >= getMinimumAmount(planId);
  };

  const plans: PaymentPlan[] = [
    {
      id: 'full-30',
      type: 'full',
      installmentCount: 1,
      dueDays: 30,
      title: language === 'ar' ? 'دفع كامل - 30 يوم' : 'Full Payment - 30 Days',
      description: language === 'ar' ? 'ادفع المبلغ كاملاً خلال 30 يوماً' : 'Pay the full amount within 30 days',
      features: [
        language === 'ar' ? 'مهلة 30 يوم للدفع' : '30-day payment period',
        language === 'ar' ? 'تذكير بالإيميل' : 'Email reminders',
        language === 'ar' ? 'دعم فني مجاني' : 'Free technical support'
      ]
    },
    {
      id: 'full-60',
      type: 'full',
      installmentCount: 1,
      dueDays: 60,
      title: language === 'ar' ? 'دفع كامل - 60 يوم' : 'Full Payment - 60 Days',
      description: language === 'ar' ? 'ادفع المبلغ كاملاً خلال 60 يوماً' : 'Pay the full amount within 60 days',
      features: [
        language === 'ar' ? 'مهلة 60 يوم للدفع' : '60-day payment period',
        language === 'ar' ? 'تذكيرات متعددة' : 'Multiple reminders',
        language === 'ar' ? 'مرونة أكثر في الدفع' : 'More payment flexibility'
      ],
      popular: true
    },
    {
      id: 'installments-2',
      type: 'installments',
      installmentCount: 2,
      dueDays: 60,
      title: language === 'ar' ? 'قسطين - 60 يوم' : '2 Installments - 60 Days',
      description: language === 'ar' ? 'اقسم المبلغ على دفعتين' : 'Split the amount into 2 payments',
      features: [
        language === 'ar' ? `القسط الأول: ${(totalAmount / 2).toLocaleString()} ريال` : `First: ${(totalAmount / 2).toLocaleString()} SAR`,
        language === 'ar' ? `القسط الثاني: ${(totalAmount / 2).toLocaleString()} ريال` : `Second: ${(totalAmount / 2).toLocaleString()} SAR`,
        language === 'ar' ? 'فترة 30 يوم بين الأقساط' : '30-day gap between installments'
      ]
    },
    {
      id: 'full-90',
      type: 'full',
      installmentCount: 1,
      dueDays: 90,
      title: language === 'ar' ? 'دفع كامل - 90 يوم' : 'Full Payment - 90 Days',
      description: language === 'ar' ? 'ادفع المبلغ كاملاً خلال 90 يوماً' : 'Pay the full amount within 90 days',
      features: [
        language === 'ar' ? 'مهلة 90 يوم للدفع' : '90-day payment period',
        language === 'ar' ? 'أقصى مرونة في الدفع' : 'Maximum payment flexibility',
        language === 'ar' ? 'تذكيرات دورية منتظمة' : 'Regular periodic reminders'
      ]
    },
    {
      id: 'installments-3',
      type: 'installments',
      installmentCount: 3,
      dueDays: 90,
      title: language === 'ar' ? '3 أقساط - 90 يوم' : '3 Installments - 90 Days',
      description: language === 'ar' ? 'اقسم المبلغ على 3 دفعات' : 'Split the amount into 3 payments',
      features: [
        language === 'ar' ? `كل قسط: ${(totalAmount / 3).toLocaleString()} ريال` : `Each: ${(totalAmount / 3).toLocaleString()} SAR`,
        language === 'ar' ? 'مرونة أكثر في الدفع' : 'Maximum payment flexibility',
        language === 'ar' ? 'فترة 30 يوم بين الأقساط' : '30-day gap between installments'
      ]
    }
  ];

  const selectedPlanDetails = plans.find(plan => plan.id === selectedPlan);

  // Auto-select first enabled plan if current selection is disabled
  useEffect(() => {
    if (!isPlanEnabled(selectedPlan)) {
      const firstEnabledPlan = plans.find(plan => isPlanEnabled(plan.id));
      if (firstEnabledPlan) {
        setSelectedPlan(firstEnabledPlan.id);
      }
    }
  }, [totalAmount, selectedPlan]);

  const handleSubmit = () => {
    if (selectedPlanDetails) {
      onPlanSelected(
        selectedPlanDetails.type,
        selectedPlanDetails.installmentCount,
        selectedPlanDetails.dueDays
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-foreground">
          {language === 'ar' ? 'اختر خطة الدفع المؤجل' : 'Choose Your Payment Plan'}
        </h3>
        <p className="text-muted-foreground mt-2">
          {language === 'ar' 
            ? `المبلغ الإجمالي: ${totalAmount.toLocaleString()} ريال سعودي`
            : `Total Amount: ${totalAmount.toLocaleString()} SAR`
          }
        </p>
      </div>

      <RadioGroup value={selectedPlan} onValueChange={setSelectedPlan}>
        <div className="grid gap-4">
          {plans.map((plan) => {
            const isEnabled = isPlanEnabled(plan.id);
            const minimumAmount = getMinimumAmount(plan.id);
            
            return (
              <div key={plan.id} className="relative">
                <Label htmlFor={plan.id} className={`cursor-pointer ${!isEnabled ? 'cursor-not-allowed opacity-50' : ''}`}>
                  <Card className={`transition-all duration-200 hover:shadow-md ${
                    selectedPlan === plan.id 
                      ? 'border-primary shadow-md bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  } ${!isEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem 
                            value={plan.id} 
                            id={plan.id} 
                            disabled={!isEnabled}
                          />
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              {plan.type === 'full' ? <CreditCard className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
                              {plan.title}
                              {plan.popular && (
                                <Badge className="bg-primary text-primary-foreground">
                                  {language === 'ar' ? 'الأكثر شيوعاً' : 'Most Popular'}
                                </Badge>
                              )}
                              {!isEnabled && (
                                <Badge variant="secondary" className="bg-destructive/10 text-destructive">
                                  {language === 'ar' ? 'غير متاح' : 'Not Available'}
                                </Badge>
                              )}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                              {plan.description}
                            </p>
                            {!isEnabled && (
                              <p className="text-xs text-destructive mt-1">
                                {language === 'ar' 
                                  ? `الحد الأدنى: ${minimumAmount.toLocaleString()} ريال`
                                  : `Minimum: ${minimumAmount.toLocaleString()} SAR`
                                }
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-foreground">
                            {plan.type === 'full' 
                              ? `${totalAmount.toLocaleString()} SAR`
                              : `${(totalAmount / plan.installmentCount).toLocaleString()} SAR`
                            }
                          </div>
                          {plan.type === 'installments' && (
                            <div className="text-sm text-muted-foreground">
                              {language === 'ar' ? 'لكل قسط' : 'per installment'}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4 text-primary" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Label>
              </div>
            );
          })}
        </div>
      </RadioGroup>

      {selectedPlanDetails && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-primary mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-semibold text-foreground">
                  {language === 'ar' ? 'معلومات إضافية' : 'Additional Information'}
                </h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    {language === 'ar' 
                      ? 'ستتلقى تذكيرات بالإيميل قبل تاريخ الاستحقاق'
                      : 'You will receive email reminders before due dates'
                    }
                  </p>
                  <p>
                    {language === 'ar' 
                      ? 'يمكنك رفع إثبات الدفع بعد كل تحويل'
                      : 'You can upload payment proof after each transfer'
                    }
                  </p>
                  <p>
                    {language === 'ar' 
                      ? 'معلومات التحويل البنكي ستكون متاحة في لوحة التحكم'
                      : 'Bank transfer details will be available in your dashboard'
                    }
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms acceptance checkbox */}
      <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
        <Checkbox
          id="terms-acceptance"
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked === true)}
          className="mt-0.5"
        />
        <Label htmlFor="terms-acceptance" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
          {language === 'ar' 
            ? 'لقد قرأت وأوافق على شروط وأحكام الدفع المؤجل، بما في ذلك مواعيد الاستحقاق والرسوم المترتبة على التأخير في الدفع.'
            : 'I have read and accept the deferred payment terms and conditions, including the due dates and any late payment fees that may apply.'
          }
        </Label>
      </div>

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {language === 'ar' ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={!selectedPlan || !termsAccepted || isSubmitting}
          className="min-w-[120px]"
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              {language === 'ar' ? 'جاري المعالجة...' : 'Processing...'}
            </div>
          ) : (
            language === 'ar' ? 'تأكيد الخطة' : 'Confirm Plan'
          )}
        </Button>
      </div>
    </div>
  );
};